import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';

export interface ExpiringItem {
  item_id: string;
  item_code: string;
  item_name: string;
  batch_number: string;
  location_name: string;
  quantity: number;
  expiry_date: string;
  days_until_expiry: number;
  urgency: 'low' | 'medium' | 'high' | 'critical';
  status: string;
  received_date?: string;
}

export interface ExpiryAlert {
  alert_id: string;
  item_id: string;
  item_code: string;
  item_name: string;
  batch_number?: string;
  location_name?: string;
  quantity: number;
  expiry_date?: string;
  alert_type: string;
  urgency: string;
  days_until_expiry: number;
  is_acknowledged: boolean;
  created_at: string;
}

export interface QuarantineItem {
  quarantine_id: string;
  item_id: string;
  item_code: string;
  item_name: string;
  location_name: string;
  batch_number?: string;
  quantity: number;
  reason: string;
  notes?: string;
  status: string;
  created_at: string;
  resolved_by?: string;
  resolved_at?: string;
}

export interface ExpiryDashboard {
  total_expiring_30_days: number;
  total_expiring_7_days: number;
  total_expired: number;
  expiring_items: ExpiringItem[];
  expired_items: ExpiringItem[];
}

export interface FEFOPickItem {
  batch_id: string;
  batch_number: string;
  expiry_date: string;
  available_quantity: number;
  quantity_to_pick: number;
  location_id: string;
}

export function useExpiry() {
  const queryClient = useQueryClient();

  const getDashboard = () =>
    useQuery<ExpiryDashboard>({
      queryKey: ['expiry-dashboard'],
      queryFn: () => api.get('/expiry/dashboard').then((res) => res.data),
    });

  const getAlerts = (params?: { include_acknowledged?: boolean; alert_type?: string }) =>
    useQuery<ExpiryAlert[]>({
      queryKey: ['expiry-alerts', params],
      queryFn: () => api.get('/expiry/alerts', { params }).then((res) => res.data),
    });

  const getQuarantineZone = (params?: { status?: string }) =>
    useQuery<QuarantineItem[]>({
      queryKey: ['quarantine-zone', params],
      queryFn: () => api.get('/expiry/quarantine', { params }).then((res) => res.data),
    });

  const getFEFOPicks = (itemId: string, quantity: number, locationId?: string) =>
    useQuery<FEFOPickItem[]>({
      queryKey: ['fefo-picks', itemId, quantity, locationId],
      queryFn: () =>
        api.get('/expiry/fefo', { params: { item_id: itemId, quantity, location_id: locationId } }).then(
          (res) => res.data
        ),
      enabled: !!itemId && quantity > 0,
    });

  const acknowledgeAlert = () =>
    useMutation({
      mutationFn: (alertId: string) => api.post(`/expiry/alerts/${alertId}/acknowledge`),
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['expiry-alerts'] });
        queryClient.invalidateQueries({ queryKey: ['expiry-dashboard'] });
      },
    });

  const addToQuarantine = () =>
    useMutation({
      mutationFn: (data: {
        item_id: string;
        quantity: number;
        reason: string;
        location_id: string;
        batch_id?: string;
        notes?: string;
      }) => api.post('/expiry/quarantine', data),
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['quarantine-zone'] });
        queryClient.invalidateQueries({ queryKey: ['expiry-dashboard'] });
      },
    });

  const disposeQuarantine = () =>
    useMutation({
      mutationFn: ({ quarantineId, notes }: { quarantineId: string; notes?: string }) =>
        api.post(`/expiry/quarantine/${quarantineId}/dispose`, { notes }),
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['quarantine-zone'] });
        queryClient.invalidateQueries({ queryKey: ['expiry-dashboard'] });
      },
    });

  const releaseQuarantine = () =>
    useMutation({
      mutationFn: ({ quarantineId, notes }: { quarantineId: string; notes?: string }) =>
        api.post(`/expiry/quarantine/${quarantineId}/release`, { notes }),
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['quarantine-zone'] });
        queryClient.invalidateQueries({ queryKey: ['expiry-dashboard'] });
      },
    });

  const consumeFEFO = () =>
    useMutation({
      mutationFn: (data: { item_id: string; quantity: number; location_id?: string; notes?: string }) =>
        api.post('/expiry/consume-fefo', data),
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['expiry-dashboard'] });
        queryClient.invalidateQueries({ queryKey: ['inventory'] });
      },
    });

  return {
    getDashboard,
    getAlerts,
    getQuarantineZone,
    getFEFOPicks,
    acknowledgeAlert,
    addToQuarantine,
    disposeQuarantine,
    releaseQuarantine,
    consumeFEFO,
  };
}
